import { CSSProperties, Fragment, useCallback, useEffect, useRef, useState } from "react";
import { ClaudeMark, SMark, Tick } from "./icons";
import { PageCtx } from "./ctx";
import { asset, AVATARS, FAN_BACK_SHOTS, PDF_PAGES, Tile, TILES, TilePop } from "./data";
import PhoneFrame from "./PhoneFrame";
import { TILE_ICON_PATHS } from "./tileIcons";

const WALL_LINES = [1, 2, 3, 4];
const PAIN_TAGS = [1, 2, 3, 4];
const AUTO_ADVANCE_MS = 3000;

type PopEntry = { n: number } & TilePop;

function Pop({ tile, pop, index, ctx }: { tile: Tile; pop: PopEntry; index: number; ctx: PageCtx }) {
	const { tr } = ctx;
	const key = `tile.${tile.id}.pop${pop.n}`;
	// The PDF tile's page stack is the first child of the pop-up layer, so its chips animate one beat later.
	const delay = `${0.18 + (index + (tile.paper ? 1 : 0)) * 0.14}s`;
	const side = pop.pos.left !== undefined ? "left" : "right";
	const style: CSSProperties & { "--r"?: string } = { top: pop.pos.top, animationDelay: delay };
	if (pop.pos.left !== undefined) style.left = pop.pos.left;
	if (pop.pos.right !== undefined) style.right = pop.pos.right;
	if ("rot" in pop) style["--r"] = `${pop.rot}deg`;

	switch (pop.kind) {
		case "chip":
			return (
				<div className="pop pop-chip" style={style} data-side={side}>
					<i style={pop.dot === "check" ? undefined : { background: pop.dot }}>{pop.dot === "check" && <Tick width={3.2} />}</i>
					{tr(key)}
				</div>
			);
		case "avatarChip":
			return (
				<div className="pop pop-chip avs" style={style} data-side={side}>
					<span className="st">
						{AVATARS.map((avatar) => (
							<img key={avatar} src={asset(avatar)} alt="" />
						))}
					</span>
					{tr(key)}
				</div>
			);
		case "card":
			return (
				<div className="pop pop-card" style={style} data-side={side}>
					<img src={asset(pop.img)} alt="" />
					<b>{tr(`${key}.title`)}</b>
					<small>{tr(`${key}.sub`)}</small>
				</div>
			);
		case "photo":
			return (
				<div className="pop photo" style={style} data-side={side}>
					<img src={asset(pop.img)} alt="" />
				</div>
			);
		case "bubble":
			return (
				<div className="pop bubble" style={style} data-side={side}>
					<img src={asset(pop.avatar)} alt="" />
					<span>
						<b>{tr(`${key}.name`)}</b>
						{tr(`${key}.text`)}
					</span>
				</div>
			);
	}
}

/**
 * "Chat to dream it. Stippl to take it.": the long-scroll chat on one side, the saved trip on the other, with the
 * eleven tools as tiles under a phone. Tapping (or hovering) a tile puts that tool on the phone, with pop-ups; the
 * tiles also advance on their own every three seconds unless the pointer is over them or motion is reduced.
 */
export default function Spar({ ctx }: { ctx: PageCtx }) {
	const { assistant, name, tr, promptHref, trackCta } = ctx;
	const sparRef = useRef<HTMLElement>(null);
	const tripRef = useRef<HTMLDivElement>(null);
	const [cur, setCur] = useState(0);
	// Pop-ups and the swap animation only start once a tile has been picked, as in the designer's page.
	const [picked, setPicked] = useState(false);
	const [swapKey, setSwapKey] = useState(0);
	const curRef = useRef(0);
	const pausedRef = useRef(false);
	const reduceRef = useRef(false);
	const autoRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const turnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const pick = useCallback((i: number) => {
		curRef.current = i;
		setCur(i);
		setPicked(true);
		if (!reduceRef.current) setSwapKey((k) => k + 1);
	}, []);

	const startAuto = useCallback(() => {
		if (reduceRef.current) return;
		if (autoRef.current) clearInterval(autoRef.current);
		autoRef.current = setInterval(() => {
			if (!pausedRef.current) pick((curRef.current + 1) % TILES.length);
		}, AUTO_ADVANCE_MS);
	}, [pick]);

	/** "Turn it into a trip": replay the wall-of-text-to-rows animation, show the first tool, then let the tiles run. */
	const turn = useCallback(() => {
		const spar = sparRef.current;
		if (spar) {
			spar.classList.remove("play");
			void spar.offsetWidth; // restart the CSS animation
			spar.classList.add("play");
		}
		pick(0);
		if (turnTimerRef.current) clearTimeout(turnTimerRef.current);
		turnTimerRef.current = setTimeout(startAuto, reduceRef.current ? 0 : 1400);
	}, [pick, startAuto]);

	useEffect(() => {
		reduceRef.current = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		TILES.forEach((tile) => {
			new Image().src = asset(tile.shot);
		});
		startAuto();
		let observer: IntersectionObserver | undefined;
		if ("IntersectionObserver" in window && tripRef.current) {
			observer = new IntersectionObserver(
				(entries) => {
					if (entries[0].isIntersecting) {
						turn();
						observer?.disconnect();
					}
				},
				{ threshold: 0, rootMargin: "0px 0px -30% 0px" }
			);
			observer.observe(tripRef.current);
		}
		return () => {
			observer?.disconnect();
			if (autoRef.current) clearInterval(autoRef.current);
			if (turnTimerRef.current) clearTimeout(turnTimerRef.current);
		};
	}, [startAuto, turn]);

	const canHover = () => typeof window.matchMedia === "function" && window.matchMedia("(hover:hover)").matches;
	const tile = TILES[cur];

	return (
		<section className="spar" aria-labelledby="atp-spar-h" ref={sparRef}>
			<div className="wrap">
				<div className="sec-head center">
					<p className="eyebrow">{tr("spar.eyebrow")}</p>
					<h2 className="h2" id="atp-spar-h">
						{tr("spar.h2Pre")}
						<span className="hl">{tr("spar.h2Hl")}</span>
					</h2>
					<p className="lede">{tr("spar.lede")}</p>
				</div>
				<div className="spar-grid">
					<div className="pain">
						{PAIN_TAGS.map((n) => (
							<span className={`pain-tag t${n}`} key={n}>
								{tr(`spar.pain${n}`)}
							</span>
						))}
						<div className="minichat" aria-hidden="true">
							<div className="mc2-top">
								{assistant === "claude" ? <ClaudeMark /> : <span className="mc2-logo" />}
								{name}
							</div>
							<div className="mc2-u">{tr("spar.miniUser")}</div>
							<div className="mc2-a">
								<span className="ai-dot">
									<svg viewBox="0 0 12 12">
										<circle cx="6" cy="6" r="3" fill="#fff" />
									</svg>
								</span>
								<div className="wall">
									{WALL_LINES.map((n) => (
										<Fragment key={n}>
											{tr(`spar.wall${n}`)}
											{n < WALL_LINES.length && <br />}
										</Fragment>
									))}
								</div>
							</div>
							<div className="mc2-in">
								{tr("chat.composerPlaceholder")}
								<span>
									<svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
										<path d="M12 19V5M5 12l7-7 7 7" />
									</svg>
								</span>
							</div>
						</div>
						<p className="pain-cap">{tr("spar.painCaption")}</p>
					</div>
					<div className="spar-arrow">
						<svg className="route-down" viewBox="0 0 20 120" aria-hidden="true">
							<path d="M10 0 C 18 30, 2 60, 10 120" fill="none" stroke="#13c892" strokeWidth="3" strokeLinecap="round" strokeDasharray="0.1 9" />
						</svg>
						<button type="button" className="turn" onClick={turn}>
							<svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
								<path d="M5 12h14M13 6l6 6-6 6" />
							</svg>
							<span>{tr("spar.turn")}</span>
						</button>
					</div>
					<div className="spar-col trip" id="atp-tripcard" ref={tripRef}>
						<div className="tc-top">
							<div className="tc-copy">
								<p className="tc-eyebrow">{tr("trip.eyebrow")}</p>
								<h3>
									{tr("trip.h3Line1")}
									<br />
									<em>{tr("trip.h3Line2")}</em>
								</h3>
								<p>{tr("trip.copy")}</p>
								<p className="tc-hint">{tr("trip.hint")}</p>
							</div>
							<div className="tc-stage" aria-live="polite">
								<div className="fan-back" aria-hidden="true">
									{FAN_BACK_SHOTS.map((shot, i) => (
										<PhoneFrame key={shot} className={`b${i + 1}`} statusBar={false} img={{ src: asset(shot) }} />
									))}
								</div>
								<div className="front-wrap">
									{/* keyed on swapKey so each pick remounts the frame and replays its swap animation */}
									<PhoneFrame
										key={swapKey}
										id="atp-tc-frame"
										className={swapKey ? "swap" : ""}
										style={tile.paper ? { visibility: "hidden" } : undefined}
										img={{ src: asset(tile.shot), alt: tr(`tile.${tile.id}.alt`) }}
									/>
									<div className="pops" aria-hidden="true">
										{picked && tile.paper && (
											<div className="pdfstack" key={`pdf-${cur}-${swapKey}`}>
												{PDF_PAGES.map((page, i) => (
													<img key={page} className={`pg p${i + 1}`} src={asset(page)} alt="" />
												))}
											</div>
										)}
										{picked && tile.pops.map((pop, i) => <Pop key={`${cur}-${swapKey}-${pop.n}`} tile={tile} pop={pop} index={i} ctx={ctx} />)}
									</div>
								</div>
							</div>
						</div>
						<div
							className="frows"
							onMouseEnter={() => {
								if (canHover()) pausedRef.current = true;
							}}
							onMouseLeave={() => {
								pausedRef.current = false;
								startAuto();
							}}
						>
							{TILES.map((t, i) => (
								<button
									type="button"
									className="frow"
									key={t.id}
									data-i={i}
									style={{ "--k": i } as CSSProperties}
									aria-pressed={i === cur}
									onClick={() => {
										pick(i);
										startAuto();
									}}
									onMouseEnter={() => {
										if (canHover()) pick(i);
									}}
								>
									<span className={`ti ${t.tone}`}>
										<svg viewBox="0 0 32 32" aria-hidden="true">
											<path d={TILE_ICON_PATHS[t.id]} fill="currentColor" />
										</svg>
									</span>
									<span className="ft">
										<b>{tr(`tile.${t.id}.title`)}</b>
										<span>{tr(`tile.${t.id}.sub`)}</span>
									</span>
								</button>
							))}
							<a
								className="frow cta-card"
								href={promptHref(tr("prompt.thailandJuly"))}
								target="_blank"
								rel="noopener"
								data-where="features"
								onClick={() => trackCta("features", "open_assistant")}
							>
								<span className="ti">
									<SMark />
								</span>
								<span className="ft">
									<b>{tr("tile.cta.title")}</b>
									<span>{tr("tile.cta.sub")}</span>
								</span>
							</a>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
